<template>
  <div>
    <div v-if="!isLoading">
      <!-- <div id="app"> -->
      <div :class="['app', `app-${$mq}`]">
        <navbar />
        <sidenav />

        <opacity-cover v-if="navOpen" />

        <!-- Small device layout -->
        <mq-layout :mq="['mobile', 'tablet']"> <router-view /> </mq-layout>

        <!-- Desktop layout -->
        <div :class="['Router-Wrapper', routerClass, routerAuth]" v-if="$mq === 'desktop'">
          <mq-layout mq="desktop"> <router-view :key="$route.fullPath" /> </mq-layout>
        </div>

        <snackbar />
        <notification-toaster />
        <simplert></simplert>

        <tawkto></tawkto>
      </div>
    </div>

    <div v-else class="loading">
      <div class="loading-logo">
        <img src="@/assets/logo.png" />
      </div>

      <div class="loading-spinner">
        <Spinner />
      </div>

      <div class="loading-text">
        Le contenu est en cours de téléchargement, merci de patienter...
      </div>
    </div>
  </div>
</template>

<script>
import differenceInDays from 'date-fns/differenceInDays'
import isNil from 'lodash/isNil'

import { auth } from '@/services/firebase'
import '@/services/staticAssetPreLoader'

import { SET_USER_LOGGED, SET_USER_INFORMATIONS, LOAD_COMMANDS } from '@/store/modules/user/types'

import { INIT_SHOP } from '@/store/modules/shop/types'

import userMixin from '@/mixins/user'
import completionMixin from '@/mixins/completion'

import {
  OPEN_NAVIGATION_DRAWER,
  CLOSE_NAVIGATION_DRAWER,
  SNACKBAR_MESSAGE
} from '@/helpers/eventBus'
import { getHydratedUser } from '@/helpers/userHelper'
import remoteConfig from '@/lib/remote-config'
import { show_splash_screen } from '@/lib/remote-config/constants'
import '@/lib/video'
import '@/lib/podcast'

import Navbar from '@/components/layout/Navbar'
import Sidenav from '@/components/layout/Sidenav'
import OpacityCover from '@/components/layout/OpacityCover'
import Snackbar from '@/components/layout/Snackbar'
import Spinner from '@/components/layout/Spinner'
import Tawkto from '@/components/layout/Tawkto'
import DesktopBreadcrumb from '@/components/navigation/DesktopBreadcrumb'
import NotificationToaster from '@/components/notification/NotificationToaster'

export default {
  name: 'App',
  mixins: [userMixin, completionMixin],
  components: {
    Spinner,
    Snackbar,
    Sidenav,
    Navbar,
    OpacityCover,
    DesktopBreadcrumb,
    NotificationToaster,
    Tawkto,
  },
  data: () => ({
    isLoading: true,
    navOpen: false,
    contentSyncNotified: false,
    // SW Fields
    updateAvailable: false,
    swRegistration: null,
    refreshing: false
  }),
  computed: {
    lastSyncDate() {
      const { lastSyncDate } = this.$store.state.system

      return lastSyncDate ? new Date(lastSyncDate) : null
    },
    hasCachedContent() {
      return !isNil(this.lastSyncDate)
    },
    cacheAge() {
      if (!this.lastSyncDate) return -1

      return differenceInDays(new Date(), this.lastSyncDate)
    },
    contentSynced() {
      return this.$store.state.system.contentSync
    },
    routerClass() {
      return `Router-${process.env.VUE_APP_APP_NAME}`
    },
    routerAuth() {
      return this.isUserLogged ? 'Router-logged' : 'Router-Nologged'
    },
  },
  methods: {
    loadeShopify() {
      if (this.$store.state.system.contentSync) {
        this.$store.dispatch(INIT_SHOP)
      } else {
        setTimeout(()=>{
          this.loadeShopify()
        }, 250);
      }
    },
    displayToastSWRefresh(registration) {
      this.swRegistration = registration.detail
      this.updateAvailable = true

      const intervalId = setInterval(() => {
        if (this.isLoading) return

        clearInterval(intervalId)
        this.$eventBus.$emit(SNACKBAR_MESSAGE, {
          message: this.$t('system.new_version_available'),
          action: this.refreshApp,
          infinite: true,
          bypass: true
        })
      }, 1001)
    },

    refreshApp() {
      this.updateAvailable = false

      // Protect against missing registration.waiting.
      if (!this.swRegistration || !this.swRegistration.waiting) return

      // SkipWaiting to force reinstall of the SW
      // By triggering the skipWaiting, we trigger 'controllerChange' wrote below that reload the page
      this.swRegistration.waiting.postMessage('skipWaiting')
    }
  },
  mounted: async function() {
    // We must wait the initialize function
    // We can use the vue-router as it is not fully initialized when coming here
    const path = window.location.href
      .split('/')
      .slice(3)
      .pop()
    const showSplashScreen =
      (remoteConfig.getBoolean(show_splash_screen) || false) &&
      ['', 'splashScreen'].includes(path) &&
      sessionStorage.getItem('splashShown') === null

    if (showSplashScreen) {
      this.$router.replace('/splashScreen')
    }

    auth.onAuthStateChanged(async user => {
      const hydratedUser = user ? await getHydratedUser(user.uid) : {}
      // let locale = localStorage.getItem('language') || hydratedUser.language || navigator.language
      const locale = 'fr-FR'
      await this.$store.dispatch('SYNC_DATA', { locale })
      const i18nLocaleMapping = {
        'en-US': 'en',
        'fr-FR': 'fr'
      }

      this.$root.$i18n.locale = i18nLocaleMapping[locale]
      try {
        if (this.$validator.dictionary.hasLocale(i18nLocaleMapping[locale])) {
          this.$validator.localize(i18nLocaleMapping[locale]);
        } else {
          import(`vee-validate/dist/locale/${i18nLocaleMapping[locale]}`).then(locale => {
            this.$validator.localize(i18nLocaleMapping[locale], locale);
          });
        }
      } catch (error) {
        console.log('error vee validate localisation', error)
      }
      
      const userIsLogged = !!user

      this.$store.commit(SET_USER_LOGGED, userIsLogged)

      if (userIsLogged) {
        this.$store.commit(SET_USER_INFORMATIONS, hydratedUser)
        this.loadeShopify()

        // const [error, response] = await this.$store.dispatch(CHECK_SUBSCRIPTION)

        // if (error || response.data.error) {
        //   this.$eventBus.$emit(SNACKBAR_MESSAGE, {
        //     message: this.$t('errors.custom/internal-error')
        //   })
        // }

        // purposely dont await this to do it asynchronously
        this.$store.dispatch(LOAD_COMMANDS)

        this.hydrateCompletion()
      }
      // DONE LOADING
      this.isLoading = false
    })

    this.$eventBus.$on(OPEN_NAVIGATION_DRAWER, () => {
      this.navOpen = true
    })

    this.$eventBus.$on(CLOSE_NAVIGATION_DRAWER, () => {
      this.navOpen = false
    })

    document.addEventListener('swUpdated', this.displayToastSWRefresh, { once: true })

    // Refresh all open app tabs when a new service worker is installed.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!this.refreshing) {
        this.refreshing = true
        window.location.reload()
      }
      return
    })

    window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault()

      window.customSystem = {}
      window.customSystem.deferedPrompt = e
    })
    // const nav = document.querySelector('.navbarMobile')

    // window.addEventListener('scroll', () => {
    //   console.log('scroll', nav)
    // })
  },
  updated() {
    if (this.contentSyncNotified) return

    if (!this.hasCachedContent && !this.contentSynced) {
      // Content did not download
      // this.$eventBus.$emit(SNACKBAR_MESSAGE, { message: this.$t('system.no_cache') }).
      console.log(this.$t('system.no_cache'))
    } else if (this.hasCachedContent && !this.contentSynced && this.cacheAge >= 1) {
      // Serving content from cache
      // this.$eventBus.$emit(SNACKBAR_MESSAGE, { message: this.$tc('system.cache_served', this.cacheAge) })
      console.log(this.$tc('system.cache_served', this.cacheAge))
    } else {
      // Content synced at this session
      // this.$eventBus.$emit(SNACKBAR_MESSAGE, { message: this.$t('system.cache_refreshed') })
      console.log(this.$t('system.cache_refreshed'))
    }

    this.contentSyncNotified = true
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/_variables.scss';

body {
  background-color: #F9FAFF;
}

#app {
  height: 100%; // 40px for footer on desktop
  font-family: $font-family-app;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $font-color;
}
.app-mobile, .app-tablet {
  margin-top: 53px;
}

#nav {
  padding: 30px;
  a {
    font-weight: bold;
    color: $font-color;
    &.router-link-exact-active {
      color: $secondary;
    }
  }
}

body {
  margin: 0;
  padding: 0;
  font-family: $font-family-app;
}

hr {
  opacity: 0.18;
  border: solid 1px #4a4a4a;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 100vh;
}

.loading-spinner {
  margin-top: 35px;
}

.loading-logo img {
  height: 100px;
  width: auto;
}

.loading-text {
  margin-top: 100px;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
}

.Router-Wrapper {
  display: inline-block;
  width: calc(100% - 360px);

  &.Router-vinovibes {
    &.Router-logged {
      width: calc(100% - 250px);
      margin-left: 250px;
      margin-top: 80px;
    }
    
    &.Router-Nologged {
      width: 100%;
      margin-top: 110px;
      margin-left: 0px;
    }
  }
}



// CSS FOR HOOPER CAROUSSEL
.hooper-slide {
  flex-shrink: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}
.hooper-progress {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 4px;
  background-color: #efefef;
}
.hooper-progress-inner {
  height: 100%;
  background-color: #4285f4;
  transition: 300ms;
}
.hooper-pagination {
  position: absolute;
  bottom: 0;
  right: 50%;
  transform: translateX(50%);
  display: flex;
  padding: 5px 10px;
}
.hooper-indicators {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.hooper-indicator {
  margin: 0 2px;
  width: 12px;
  height: 4px;
  border-radius: 4px;
  border: none;
  padding: 0;
  background-color: #fff;
  cursor: pointer;
}
.hooper-pagination.is-vertical {
  bottom: auto;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}
.hooper-pagination.is-vertical .hooper-indicators {
  flex-direction: column;
}
.hooper-pagination.is-vertical .hooper-indicator {
  width: 6px;
}
.hooper-next,
.hooper-prev {
  background-color: transparent;
  border: none;
  padding: 1em;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}
.hooper-next.is-disabled,
.hooper-prev.is-disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.hooper-next {
  right: 0;
}
.hooper-prev {
  left: 0;
}
.hooper-navigation.is-vertical .hooper-next {
  top: auto;
  bottom: 0;
  transform: initial;
}
.hooper-navigation.is-vertical .hooper-prev {
  top: 0;
  bottom: auto;
  right: 0;
  left: auto;
  transform: initial;
}
.hooper-navigation.is-rtl .hooper-prev {
  left: auto;
  right: 0;
}
.hooper-navigation.is-rtl .hooper-next {
  right: auto;
  left: 0;
}
.hooper {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 200px;
}
.hooper * {
  box-sizing: border-box;
}
.hooper-list {
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.hooper-track {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
}
.hooper.is-vertical .hooper-track {
  flex-direction: column;
  height: 200px;
}

.hooper.is-rtl {
  direction: rtl;
}

.hooper-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.link {
  text-decoration: none;
  color: black;
}

.link-wrapper {
  margin-bottom: 15px;
}

.datepicker-input {
  width: 100%;
}

.topbar-wrapper {
  width: 100vw;
}
</style>

